import { MentorLink } from '../mentors/MentorScreen'
import { LocationWindow } from '../LocationWindow'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { SkillBook, SkillBookItem, SkillSchool } from '@mezha/api'
import { useSetSkillsMutation, useSkillsQuery } from '../../api/endpoints/skills'
import { useCommand } from '../../lib/useCommand'
import { usePhone } from '../../lib/useSize'
import { useShellMode } from '../../shell/ShellContext'
import { Emblem } from '../../ui/brand/Emblems'
import { Failure, Loading } from '../../ui/State'
import { PlankButton, Sheet, cx } from '../../ui'
import { GATE_RU, KIND_EMBLEM, KIND_RU, MISSING_RU, TIER_RIM, costText } from './words'
import './skills.css'

type Tab = 'schools' | 'set'
const TABS = [{ id: 'schools', label: 'Школы' }, { id: 'set', label: 'Боевой набор' }]

/** Why the next rank cannot be taken yet (SkillNext.block). */
const NEXT_RU: Record<string, string> = {
  skill_locked: 'ступень закрыта',
  mastery_required: 'не хватает мастерства',
  not_enough_skill_points: 'нет свободных очков',
}

/** `/play/skills`: the skill book (combat_system_v2.md §9, §15): schools and
 *  mastery, tiers I–V, skills with their ranks, and the combat set, saved on
 *  every press. Ranks are taken at the mentors of the schools. */
export function SkillsScreen() {
  useShellMode('window')
  const [params, setParams] = useSearchParams()
  const tab: Tab = params.get('tab') === 'set' ? 'set' : 'schools'
  const book = useSkillsQuery()
  const b = book.data
  const setTab = (t: string) => setParams(t === 'set' ? { tab: 'set' } : {}, { replace: true })
  return (
    <LocationWindow title="Умения" emblem="nav.skills" tabs={TABS} activeTab={tab} onTab={setTab} idBase="skills" bodyClassName="skills-body"
      action={b && <span className="points" title="Очки умений: одно за уровень со второго и по одному за испытания наставников"><span className="points__label">Очки умений</span> <b>{b.points.free}</b></span>}>
      {book.isLoading && <Loading />}
      {book.isError && !b && <Failure error={book.error} retry={() => void book.refetch()} what="Книга умений" />}
      {b && (tab === 'schools' ? <Schools book={b} /> : <CombatSet book={b} refetch={() => void book.refetch()} />)}
    </LocationWindow>
  )
}

function MasteryBar({ s }: { s: SkillSchool }) {
  const m = s.mastery
  return (
    <span className="masterybar" title={`мастерство ${m.level}, потолок уровня ${m.cap}`}>
      <span className="masterybar__fill" style={{ width: `${(m.level / m.max) * 100}%` }} />
      <span className="masterybar__cap" style={{ left: `${(m.cap / m.max) * 100}%` }} />
      {s.tiers.slice(1).map(t => <span key={t.tier} className="masterybar__tick" style={{ left: `${(t.mastery / m.max) * 100}%` }} />)}
    </span>
  )
}

function Schools({ book }: { book: SkillBook }) {
  const phone = usePhone()
  const [params, setParams] = useSearchParams()
  const school = book.schools.find(s => s.id === params.get('school')) ?? book.schools[0]
  const [skillId, setSkillId] = useState<string | null>(null)
  const [card, setCard] = useState(false)
  if (!school) return <p className="note">Школ пока нет.</p>
  const skill = school.skills.find(k => k.id === skillId) ?? school.skills[0]
  const pick = (id: string) => {
    setParams({ school: id }, { replace: true })
    setSkillId(null)
  }
  const choose = (k: SkillBookItem) => {
    setSkillId(k.id)
    setCard(true)
  }
  return (
    <>
      <div className="schoolbar" role="tablist" aria-label="Школы">
        {book.schools.map(s => {
          const open = s.tiers[0]?.open
          return (
            <button key={s.id} type="button" role="tab" aria-selected={s.id === school.id} className={cx('stab', s.id === school.id && 'is-selected', !open && 'is-locked')}
              onClick={() => pick(s.id)} title={`${s.name}: ${open ? `${s.title}, мастерство ${s.mastery.level}` : 'не открыта'}`}>
              {s.icon ? <img className="em" src={s.icon} alt="" /> : <Emblem name="nav.skills" />}
              <span><b>{s.name}</b><small>{open ? `${s.title} · ${s.mastery.level}` : s.mastery.level > 0 ? `мастерство ${s.mastery.level}` : 'не открыта'}</small><MasteryBar s={s} /></span>
            </button>
          )
        })}
      </div>
      <MentorLink school={school.id} />
      <div className="skillsmain">
        <Tiers school={school} selected={skill?.id ?? null} onPick={choose} />
        {!phone && skill && <SkillCard school={school} k={skill} book={book} />}
      </div>
      {phone && skill && (
        <Sheet open={card} title={skill.name} emblem={KIND_EMBLEM[skill.kind]} onClose={() => setCard(false)}>
          <SkillCard school={school} k={skill} book={book} />
        </Sheet>
      )}
    </>
  )
}

function Ranks({ k }: { k: SkillBookItem }) {
  return (
    <span className="ranks" aria-label={`ранг ${k.rank} из ${k.max_rank}`}>
      {Array.from({ length: k.max_rank }, (_, i) => <i key={i} className={cx(i < k.rank && 'is-on')} />)}
    </span>
  )
}

function SkillNode({ k, selected, inSet, onPick }: { k: SkillBookItem; selected: boolean; inSet?: boolean; onPick: (k: SkillBookItem) => void }) {
  const sub = k.kind === 'passive' ? 'умение · всегда'
    : [KIND_RU[k.kind], costText(k.cost_focus, k.cost_mana), k.cooldown ? `↻${k.cooldown}` : '', k.once ? 'раз за бой' : ''].filter(Boolean).join(' · ')
  const locked = k.rank === 0 && k.next?.block === 'skill_locked'
  return (
    <button type="button" className={cx('snode', `snode--${k.kind}`, k.rank > 0 && 'is-learned', locked && 'is-locked', selected && 'is-selected')}
      onClick={() => onPick(k)} aria-pressed={selected}>
      <span className="snode__face"><Emblem name={KIND_EMBLEM[k.kind]} /></span>
      <span><b>{k.name}</b><small>{sub}</small></span>
      <Ranks k={k} />
      {k.next && k.next.block === '' && <span className="snode__new" title="можно взять ранг у наставника">+</span>}
      {(inSet ?? k.in_set) && <span className="snode__set" title="в боевом наборе">набор</span>}
    </button>
  )
}

function Tiers({ school, selected, onPick }: { school: SkillSchool; selected: string | null; onPick: (k: SkillBookItem) => void }) {
  const m = school.mastery
  return (
    <div className="tiers">
      <div className="tiers__school">
        {school.icon ? <img className="em" src={school.icon} alt="" /> : <Emblem name="nav.skills" />}
        <div><b>{school.name}</b><small>{school.description}</small></div>
        <div className="tiers__m">
          {school.title ? <>{school.title} · </> : null}мастерство <b>{m.level}</b> из {m.max}
          <br /><small>потолок на этом уровне — {m.cap}{!school.in_force && school.kind === 'martial' ? ' · оружие школы не в руках' : ''}</small>
        </div>
      </div>
      {school.tiers.map(t => (
        <div key={t.tier} className={cx('tier', !t.open && 'is-closed')}>
          <div className="tier__rim">
            <b>{TIER_RIM[t.tier - 1]}</b>
            <small>{t.title}<br />{t.open ? 'открыта' : `${GATE_RU[t.gate]}${t.gate === 'auto' ? `: ${t.mastery}` : ''}, ур. ${t.level}`}</small>
          </div>
          <div className="tier__skills">
            {school.skills.filter(k => k.tier === t.tier).map(k => <SkillNode key={k.id} k={k} selected={selected === k.id} onPick={onPick} />)}
          </div>
        </div>
      ))}
    </div>
  )
}

function SkillCard({ school, k, book }: { school: SkillSchool; k: SkillBookItem; book: SkillBook }) {
  const facts: string[] = []
  const price = costText(k.cost_focus, k.cost_mana)
  if (price) facts.push(price)
  if (k.cooldown) facts.push(`перезарядка ${k.cooldown}`)
  if (k.once) facts.push('раз за бой')
  if (k.requires.shield) facts.push('нужен щит')
  if (k.requires.two_handed) facts.push('двуручное оружие')
  if (k.requires.zone) facts.push(`удар только в ${k.requires.zone === 'head' ? 'голову' : k.requires.zone === 'body' ? 'корпус' : 'ноги'}`)
  if (k.kind === 'passive') facts.push('действует всегда, в набор не входит')
  const tierTitle = school.tiers[k.tier - 1]
  return (
    <div className="skillcard">
      <div className="parch">
        <div className="skillcard__head">
          <span className="snode__face"><Emblem name={KIND_EMBLEM[k.kind]} /></span>
          <div><h3>{k.name}</h3><div className="skillcard__kind">{KIND_RU[k.kind]} · ступень {TIER_RIM[k.tier - 1]} · {school.name}</div></div>
        </div>
        <div className="skillcard__facts">
          {facts.map(f => <span key={f} className="fact">{f}</span>)}
          {k.missing && <span className="fact fact--warn">{MISSING_RU[k.missing] ?? k.missing}</span>}
        </div>
        <p className="skillcard__text">{k.description}</p>
        <div className="rankrows">
          {k.ranks.map((text, i) => {
            const r = i + 1
            const next = k.next && k.next.rank === r ? k.next : null
            return (
              <div key={r} className={cx('rankrow', r <= k.rank && 'is-have', next && 'is-next')}>
                <b>{k.max_rank === 1 ? 'Ранг' : `Ранг ${r}`}</b><span>{text}</span>
                {next && <u className={cx(next.block === 'mastery_required' && 'is-bad')}>мастерство {next.need_mastery}{next.block ? ` · ${NEXT_RU[next.block] ?? next.block}` : ''}</u>}
              </div>
            )
          })}
        </div>
      </div>
      {k.next && (
        <div className="where slab">
          <Emblem name="loc.talk" />
          <span>
            {k.next.block === 'skill_locked' && tierTitle
              ? <>Ступень {TIER_RIM[k.tier - 1]} «{tierTitle.title}»: {GATE_RU[tierTitle.gate]}, мастерство {tierTitle.mastery}, уровень {tierTitle.level}. </>
              : k.next.block === 'mastery_required' ? <>Нужно мастерство {k.next.need_mastery} — у вас {school.mastery.level}. Оно растёт от ударов оружием школы и её умений. </> : null}
            Ранг берут у наставника школы за очко умений{book.points.free > 0 ? ` (свободно: ${book.points.free})` : ''}.
          </span>
        </div>
      )}
    </div>
  )
}

/** The combat set: slots of the level, learned active skills; every press saves. */
function CombatSet({ book, refetch }: { book: SkillBook; refetch: () => void }) {
  const [save, saving] = useSetSkillsMutation()
  const cmd = useCommand(refetch)
  const [slot, setSlot] = useState<number | null>(null)
  const all = book.schools.flatMap(s => s.skills.map(k => ({ s, k })))
  const byId = new Map(all.map(x => [x.k.id, x]))
  const learned = all.filter(x => x.k.rank > 0 && x.k.kind !== 'passive')
  const put = async (id: string) => {
    const set = [...book.set]
    const at = set.indexOf(id)
    const to = slot
    if (to == null) {
      if (at >= 0) set[at] = ''
      else {
        const free = set.findIndex(x => !x)
        if (free < 0) return
        set[free] = id
      }
    } else {
      if (at >= 0) set[at] = set[to] ?? ''
      set[to] = id
    }
    setSlot(null)
    await cmd.run(() => save({ expected_version: book.version, slots: set }).unwrap())
  }
  const full = !book.set.some(x => !x)
  return (
    <div className="skillsmain">
      <div className="setpane">
        <div className="eyebrow2">Боевой набор · {book.set.filter(Boolean).length} из {book.slots}</div>
        <div className="setbar">
          {book.set.map((id, i) => {
            const x = id ? byId.get(id) : undefined
            return (
              <button key={i} type="button" className={cx('setslot', !x && 'is-empty', slot === i && 'is-selected', x?.k.missing && 'is-warn')}
                aria-pressed={slot === i} onClick={() => setSlot(slot === i ? null : i)} aria-label={`Ячейка ${i + 1}: ${x ? x.k.name : 'пусто'}`}>
                <span className="setslot__n">{i + 1}</span>
                {x ? <>
                  <span className="snode__face"><Emblem name={KIND_EMBLEM[x.k.kind]} /></span>
                  <span><b>{x.k.name}</b><small>{[costText(x.k.cost_focus, x.k.cost_mana), x.k.cooldown ? `↻${x.k.cooldown}` : '', x.k.missing ? MISSING_RU[x.k.missing] : ''].filter(Boolean).join(' · ')}</small></span>
                </> : 'пусто'}
              </button>
            )
          })}
        </div>
        <p className="setnote">
          Удар, Натиск, Памятный знак и Передышка есть всегда и места не занимают; пассивные умения действуют без набора.
          Нажмите умение — оно встанет в свободную ячейку, нажмите ещё раз — уйдёт из набора. Чтобы заменить, сначала выберите ячейку. Набор сохраняется сразу.
        </p>
        {cmd.text && <p className="setwarn" role="alert">{cmd.text}</p>}
        <div className="eyebrow2">{slot != null ? `Умение для ячейки ${slot + 1}` : 'Выученные приёмы, заговоры и знаки'}</div>
        {learned.length === 0 && <p className="note">Выученных умений пока нет: ранги берут у наставников школ.</p>}
        <div className="setlist">
          {learned.map(({ s, k }) => (
            <div key={k.id} className="setline">
              <SkillNode k={k} selected={false} inSet={book.set.includes(k.id)} onPick={() => void (!saving.isLoading && (slot != null || book.set.includes(k.id) || !full) && put(k.id))} />
              <small className="setline__school">{s.name}</small>
            </div>
          ))}
        </div>
        {full && slot == null && learned.some(x => !book.set.includes(x.k.id)) && <p className="setnote">Набор полон: выберите ячейку, чтобы заменить умение.</p>}
      </div>
      {book.set.some(id => id && byId.get(id)?.k.missing) && (
        <div className="skillcard">
          <p className="setwarn">В наборе есть умения, которым не хватает оружия: в бою они будут недоступны.</p>
          <PlankButton onClick={() => setSlot(book.set.findIndex(id => id && byId.get(id)?.k.missing))}>Выбрать ячейку для замены</PlankButton>
        </div>
      )}
    </div>
  )
}
